/**
 * FathomVoyage — War Risk & Insurance Cost Overlay
 * Mock Data Service — April 2026
 *
 * Data context:
 *  - Iran-US tensions peaked Mar-Apr 2026 → Hormuz/AG premiums elevated
 *  - Houthi attacks in Red Sea resuming after brief ceasefire
 *  - Black Sea: Russia-Ukraine maritime conflict ongoing
 *  - Gulf of Guinea: piracy incidents up 18% YoY
 *
 * War risk premiums expressed as % of insured hull value per transit.
 * Based on JWC Listed Areas methodology (Lloyd's / IUA).
 */

// ─── THREAT ZONES ────────────────────────────────────────────────────────────

export const THREAT_ZONES = [
  {
    id: 'hormuz',
    name: 'Strait of Hormuz',
    shortName: 'Hormuz',
    risk: 'critical',      // critical | high | moderate | monitored
    color: '#DC2626',
    fillOpacity: 0.25,
    listed: true,           // JWC Listed Area
    listedDate: '2024-11-14',
    premium: { min: 0.40, max: 0.60, typical: 0.50 },  // % of hull value per transit
    lastIncident: '2026-04-02',
    incidentCount30d: 8,
    description: 'Highest-risk maritime chokepoint globally. Iran-US tensions Apr 2026 significantly elevated premiums. Multiple USV and UAV incidents in trailing 30 days.',
    coordinates: [
      [26.6, 56.4], [26.0, 56.7], [25.5, 57.1], [25.2, 58.0],
      [24.8, 58.5], [25.2, 58.8], [25.8, 58.4], [26.3, 57.9],
      [26.8, 57.3], [27.0, 56.8], [26.6, 56.4],
    ],
  },
  {
    id: 'arabian_gulf',
    name: 'Arabian Gulf',
    shortName: 'Arabian Gulf',
    risk: 'critical',
    color: '#DC2626',
    fillOpacity: 0.2,
    listed: true,
    listedDate: '2024-11-14',
    premium: { min: 0.35, max: 0.55, typical: 0.45 },
    lastIncident: '2026-04-01',
    incidentCount30d: 11,
    description: 'Persian Gulf waters. IRGC naval activity heightened. Tanker seizure risk elevated following Iran-US diplomatic breakdown Jan 2026.',
    coordinates: [
      [26.6, 56.4], [27.0, 56.8], [27.5, 55.9], [28.0, 54.8],
      [28.5, 53.5], [29.0, 52.0], [29.5, 50.5], [29.8, 49.0],
      [29.2, 48.2], [28.5, 48.6], [27.8, 49.8], [27.0, 51.2],
      [26.5, 52.8], [26.0, 54.2], [25.8, 55.8], [26.0, 56.4],
      [26.6, 56.4],
    ],
  },
  {
    id: 'gulf_of_oman',
    name: 'Gulf of Oman',
    shortName: 'Gulf of Oman',
    risk: 'high',
    color: '#F97316',
    fillOpacity: 0.2,
    listed: true,
    listedDate: '2024-11-14',
    premium: { min: 0.25, max: 0.40, typical: 0.32 },
    lastIncident: '2026-03-28',
    incidentCount30d: 5,
    description: 'Approach corridor to Hormuz. UAV attacks on commercial shipping. Vessels advised to maintain radar watch and report to UKMTO.',
    coordinates: [
      [25.2, 58.0], [24.8, 58.5], [24.2, 59.0], [23.5, 59.5],
      [22.8, 60.0], [22.0, 59.8], [21.5, 60.2], [21.8, 61.0],
      [22.5, 61.5], [23.2, 61.2], [24.0, 60.5], [24.8, 60.0],
      [25.5, 59.5], [25.8, 58.8], [25.2, 58.0],
    ],
  },
  {
    id: 'red_sea_north',
    name: 'Red Sea / Bab-el-Mandeb',
    shortName: 'Red Sea',
    risk: 'high',
    color: '#F97316',
    fillOpacity: 0.18,
    listed: true,
    listedDate: '2024-01-17',
    premium: { min: 0.15, max: 0.30, typical: 0.22 },
    lastIncident: '2026-04-03',
    incidentCount30d: 7,
    description: 'Houthi attacks resuming after Feb 2026 ceasefire collapse. Ballistic missiles and one-way attack drones targeting vessels with Israel links or US flag.',
    coordinates: [
      [12.5, 43.5], [13.0, 43.8], [14.0, 43.5], [15.0, 42.8],
      [17.0, 41.5], [19.0, 40.5], [21.0, 39.2], [22.5, 38.5],
      [24.0, 37.8], [25.0, 37.5], [25.0, 36.5], [23.0, 37.0],
      [21.0, 38.0], [19.0, 39.5], [17.0, 40.8], [15.0, 42.0],
      [13.5, 42.8], [12.0, 43.2], [12.5, 43.5],
    ],
  },
  {
    id: 'black_sea',
    name: 'Black Sea / Sea of Azov',
    shortName: 'Black Sea',
    risk: 'moderate',
    color: '#D97706',
    fillOpacity: 0.15,
    listed: true,
    listedDate: '2022-02-25',
    premium: { min: 0.10, max: 0.20, typical: 0.14 },
    lastIncident: '2026-03-22',
    incidentCount30d: 3,
    description: 'Russia-Ukraine maritime conflict ongoing. Mine threat in western Black Sea. Russian drone attacks on Ukrainian port infrastructure.',
    coordinates: [
      [46.5, 30.0], [47.0, 32.0], [46.8, 34.0], [46.5, 36.0],
      [46.0, 38.0], [45.5, 40.0], [44.5, 41.5], [43.0, 41.0],
      [42.0, 40.0], [41.5, 38.0], [41.8, 36.0], [42.5, 34.0],
      [43.5, 32.0], [44.5, 30.5], [46.0, 29.5], [46.5, 30.0],
    ],
  },
  {
    id: 'gulf_of_guinea',
    name: 'Gulf of Guinea',
    shortName: 'Gulf of Guinea',
    risk: 'moderate',
    color: '#D97706',
    fillOpacity: 0.12,
    listed: false,
    premium: { min: 0.05, max: 0.10, typical: 0.07 },
    lastIncident: '2026-03-30',
    incidentCount30d: 4,
    description: 'Nigeria-based piracy. Crew kidnap-for-ransom the primary risk. Most incidents within 200nm of Nigerian coast. Not a JWC Listed Area but requires separate K&R coverage.',
    coordinates: [
      [5.0, 0.0], [5.5, 3.0], [4.5, 5.0], [3.5, 7.0],
      [2.5, 8.0], [1.5, 8.5], [0.5, 8.0], [-0.5, 7.5],
      [-1.5, 7.0], [-2.0, 6.0], [-1.0, 4.5], [0.0, 3.5],
      [1.5, 2.5], [3.0, 1.5], [4.5, 0.5], [5.0, 0.0],
    ],
  },
];

// ─── INCIDENTS (March – April 2026) ──────────────────────────────────────────

export const INCIDENTS = [
  {
    id: 'INC-2604-001',
    date: '2026-04-02',
    vessel: 'PACIFIC CROWN',
    imo: '9847231',
    type: 'Suezmax Tanker',
    flag: '🇬🇷 Greece',
    zone: 'hormuz',
    zoneName: 'Strait of Hormuz',
    lat: 26.1,
    lng: 56.9,
    incidentType: 'USV Attack',
    severity: 'critical',
    source: 'UKMTO',
    description: 'Vessel sustained damage to starboard hull following USV strike at 0320 local time. Crew safe. Vessel diverted to Fujairah for damage assessment. IRGC believed responsible.',
  },
  {
    id: 'INC-2604-002',
    date: '2026-04-01',
    vessel: 'GULF NAVIGATOR',
    imo: '9612874',
    type: 'VLCC Tanker',
    flag: '🇸🇦 Saudi Arabia',
    zone: 'arabian_gulf',
    zoneName: 'Arabian Gulf',
    lat: 26.5,
    lng: 53.8,
    incidentType: 'Armed Boarding',
    severity: 'high',
    source: 'UKMTO / IMB',
    description: 'IRGC naval vessel approached and boarded tanker approximately 40nm south of Iran. Crew detained for 6 hours before vessel released. Believed to be linked to oil sanctions dispute.',
  },
  {
    id: 'INC-2604-003',
    date: '2026-04-03',
    vessel: 'MERIDIAN SPIRIT',
    imo: '9734521',
    type: 'MR Tanker',
    flag: '🇬🇧 UK',
    zone: 'red_sea_north',
    zoneName: 'Red Sea',
    lat: 13.5,
    lng: 43.7,
    incidentType: 'Ballistic Missile Near Miss',
    severity: 'high',
    source: 'UKMTO',
    description: 'Ballistic missile impacted 850m from vessel at 1742 UTC. Vessel increased speed and diverted course. No damage or casualties. Houthi forces claimed responsibility.',
  },
  {
    id: 'INC-2603-004',
    date: '2026-03-28',
    vessel: 'THALASSA HERO',
    imo: '9501233',
    type: 'Aframax Tanker',
    flag: '🇬🇷 Greece',
    zone: 'gulf_of_oman',
    zoneName: 'Gulf of Oman',
    lat: 23.4,
    lng: 59.8,
    incidentType: 'UAV Attack',
    severity: 'high',
    source: 'UKMTO',
    description: 'One-way attack drone struck vessel superstructure causing fire. Fire extinguished by crew. Two crew members with minor injuries. Vessel proceeded to Fujairah under own power.',
  },
  {
    id: 'INC-2603-005',
    date: '2026-03-26',
    vessel: 'YUAN HUA 28',
    imo: '9688421',
    type: 'Container 8000 TEU',
    flag: '🇨🇳 China',
    zone: 'red_sea_north',
    zoneName: 'Red Sea',
    lat: 15.2,
    lng: 41.9,
    incidentType: 'Drone Attack (Deflected)',
    severity: 'moderate',
    source: 'UKMTO',
    description: 'Vessel crew observed and reported two drones approaching. Electronic countermeasures deployed. One drone detonated short of vessel, second diverted away. No damage.',
  },
  {
    id: 'INC-2603-006',
    date: '2026-03-25',
    vessel: 'OLYMPIC FIGHTER',
    imo: '9412897',
    type: 'Capesize Bulker',
    flag: '🇬🇷 Greece',
    zone: 'arabian_gulf',
    zoneName: 'Arabian Gulf',
    lat: 27.1,
    lng: 51.5,
    incidentType: 'IRGC Vessel Shadowing',
    severity: 'low',
    source: 'UKMTO',
    description: 'IRGC fast craft shadowed vessel for 45 minutes through Iranian territorial waters approach. No hostile action. Considered as intimidation tactic.',
  },
  {
    id: 'INC-2603-007',
    date: '2026-03-22',
    vessel: 'NEPTUNE EXPLORER',
    imo: '9543109',
    type: 'LPG Carrier',
    flag: '🇳🇴 Norway',
    zone: 'black_sea',
    zoneName: 'Black Sea',
    lat: 44.5,
    lng: 32.5,
    incidentType: 'Mine Strike',
    severity: 'critical',
    source: 'IMB',
    description: 'Vessel struck a drifting mine approximately 40nm SW of Odessa. Significant hull damage below waterline. Crew evacuated. Vessel listed and subsequently sank. Total loss declared.',
  },
  {
    id: 'INC-2603-008',
    date: '2026-03-20',
    vessel: 'MAERSK SHENZHEN',
    imo: '9781234',
    type: 'Container 14000 TEU',
    flag: '🇩🇰 Denmark',
    zone: 'red_sea_north',
    zoneName: 'Red Sea',
    lat: 20.5,
    lng: 38.8,
    incidentType: 'Ballistic Missile (Avoided)',
    severity: 'high',
    source: 'UKMTO',
    description: 'Vessel received UKMTO advisory and altered course. Subsequent missile strike hit original projected position. Vessel unharmed. Maersk confirms continued avoidance of Red Sea.',
  },
  {
    id: 'INC-2603-009',
    date: '2026-03-18',
    vessel: 'CREST ENERGY',
    imo: '9234871',
    type: 'VLCC Tanker',
    flag: '🇮🇷 Iran',
    zone: 'hormuz',
    zoneName: 'Strait of Hormuz',
    lat: 26.0,
    lng: 57.2,
    incidentType: 'USV Near Miss',
    severity: 'moderate',
    source: 'UKMTO',
    description: 'Vessel reported USV passing within 200m at speed. USV did not detonate. Believed to be a surveillance/intimidation action. Vessel reported safely to Bandar Abbas.',
  },
  {
    id: 'INC-2603-010',
    date: '2026-03-15',
    vessel: 'GOLDEN ARCHER',
    imo: '9678902',
    type: 'MR Tanker',
    flag: '🇵🇭 Philippines',
    zone: 'gulf_of_guinea',
    zoneName: 'Gulf of Guinea',
    lat: 3.8,
    lng: 5.2,
    incidentType: 'Crew Kidnap',
    severity: 'critical',
    source: 'IMB',
    description: 'Armed pirates boarded vessel via stern. 7 crew members kidnapped. Vessel released after 2 hours. Crew returned after 18 days following ransom negotiations. K&R policy activated.',
  },
  {
    id: 'INC-2603-011',
    date: '2026-03-14',
    vessel: 'HORIZON VOYAGER',
    imo: '9812345',
    type: 'Suezmax Tanker',
    flag: '🇲🇹 Malta',
    zone: 'arabian_gulf',
    zoneName: 'Arabian Gulf',
    lat: 28.5,
    lng: 50.2,
    incidentType: 'Armed Shadowing',
    severity: 'low',
    source: 'UKMTO',
    description: 'IRGC speedboat tracked vessel for 90 minutes. Two crew members visible on deck with weapons. No hostile action taken. Incident reported to UKMTO.',
  },
  {
    id: 'INC-2603-012',
    date: '2026-03-12',
    vessel: 'SEOUL PRIDE',
    imo: '9561234',
    type: 'LNG Carrier',
    flag: '🇰🇷 South Korea',
    zone: 'hormuz',
    zoneName: 'Strait of Hormuz',
    lat: 25.8,
    lng: 57.5,
    incidentType: 'UAV Attack',
    severity: 'high',
    source: 'UKMTO',
    description: 'Two drones attacked vessel transiting northbound. First drone deflected by flares. Second struck cargo area without penetrating insulation. Cargo integrity maintained. Vessel continued to loading port.',
  },
  {
    id: 'INC-2603-013',
    date: '2026-03-10',
    vessel: 'POSEIDON BRAVE',
    imo: '9445623',
    type: 'Aframax Tanker',
    flag: '🇨🇾 Cyprus',
    zone: 'arabian_gulf',
    zoneName: 'Arabian Gulf',
    lat: 27.8,
    lng: 52.5,
    incidentType: 'Detention/Seizure',
    severity: 'critical',
    source: 'UKMTO / Iran state media',
    description: 'Vessel detained by IRGC Navy for alleged violation of Iranian territorial waters. Cargo declared as US-sanctioned. Vessel and crew held for 11 days before release following diplomatic intervention.',
  },
  {
    id: 'INC-2603-014',
    date: '2026-03-08',
    vessel: 'AFRICA MERCHANT',
    imo: '9334512',
    type: 'General Cargo',
    flag: '🇳🇬 Nigeria',
    zone: 'gulf_of_guinea',
    zoneName: 'Gulf of Guinea',
    lat: 4.2,
    lng: 6.8,
    incidentType: 'Armed Robbery',
    severity: 'moderate',
    source: 'IMB',
    description: 'Armed pirates boarded anchored vessel. Ship\'s cash and crew valuables stolen. No crew injuries. Vessel underway within 3 hours of pirates departing.',
  },
  {
    id: 'INC-2603-015',
    date: '2026-03-06',
    vessel: 'CRYSTAL PACIFIC',
    imo: '9723456',
    type: 'Container 8000 TEU',
    flag: '🇸🇬 Singapore',
    zone: 'red_sea_north',
    zoneName: 'Red Sea',
    lat: 14.8,
    lng: 42.2,
    incidentType: 'Ballistic Missile Attack',
    severity: 'critical',
    source: 'UKMTO',
    description: 'Anti-ship ballistic missile struck vessel bow. Major structural damage. No fatalities. Vessel taken under tow to Djibouti. Houthi military spokesman confirmed attack citing vessel ties to Israel.',
  },
  {
    id: 'INC-2603-016',
    date: '2026-03-05',
    vessel: 'NORSE VIKING',
    imo: '9612983',
    type: 'Capesize Bulker',
    flag: '🇳🇴 Norway',
    zone: 'black_sea',
    zoneName: 'Black Sea',
    lat: 45.2,
    lng: 31.8,
    incidentType: 'Drone Near Miss',
    severity: 'moderate',
    source: 'IMB',
    description: 'Russian maritime drone passed within 300m during transit of western Black Sea. No hostile action believed intended. Vessel proceeded to Constanta.',
  },
  {
    id: 'INC-2603-017',
    date: '2026-03-03',
    vessel: 'ENERGY COMMANDER',
    imo: '9891234',
    type: 'VLCC Tanker',
    flag: '🇸🇦 Saudi Arabia',
    zone: 'arabian_gulf',
    zoneName: 'Arabian Gulf',
    lat: 26.0,
    lng: 55.0,
    incidentType: 'USV Attack',
    severity: 'high',
    source: 'UKMTO',
    description: 'USV detonated approximately 15m from vessel hull. Pressure wave caused minor hull flexion. Cargo unaffected. Believed Iranian Revolutionary Guard operation targeting Saudi-flagged tonnage.',
  },
  {
    id: 'INC-2603-018',
    date: '2026-03-02',
    vessel: 'PACIFIC GUARDIAN',
    imo: '9567432',
    type: 'MR Tanker',
    flag: '🇯🇵 Japan',
    zone: 'gulf_of_oman',
    zoneName: 'Gulf of Oman',
    lat: 22.5,
    lng: 60.5,
    incidentType: 'UAV Near Miss',
    severity: 'moderate',
    source: 'UKMTO',
    description: 'Drone observed approaching vessel at low altitude. Vessel conducted emergency maneuver. Drone impacted sea 100m astern. No damage. Japanese MoD notified.',
  },
  {
    id: 'INC-2603-019',
    date: '2026-03-01',
    vessel: 'ISTANBUL STAR',
    imo: '9432165',
    type: 'Container 8000 TEU',
    flag: '🇹🇷 Turkey',
    zone: 'black_sea',
    zoneName: 'Black Sea',
    lat: 43.5,
    lng: 33.5,
    incidentType: 'Mine Strike',
    severity: 'high',
    source: 'IMB',
    description: 'Vessel struck drifting mine in central Black Sea. Flooding in forward hold. Crew safe. Emergency response team boarded. Vessel escorted to Zonguldak for repair.',
  },
  {
    id: 'INC-2603-020',
    date: '2026-03-30',
    vessel: 'AMAZON SEAGULL',
    imo: '9654321',
    type: 'General Cargo',
    flag: '🇧🇷 Brazil',
    zone: 'gulf_of_guinea',
    zoneName: 'Gulf of Guinea',
    lat: 3.5,
    lng: 7.2,
    incidentType: 'Armed Boarding',
    severity: 'moderate',
    source: 'IMB',
    description: 'Armed pirates boarded while underway 60nm offshore. Engine room taken over. Cargo surveyed and partial cargo theft. Crew locked in cabin but unharmed. Pirates departed after 4 hours.',
  },
  {
    id: 'INC-2604-021',
    date: '2026-04-01',
    vessel: 'ATLAS PIONEER',
    imo: '9754321',
    type: 'Suezmax Tanker',
    flag: '🇬🇷 Greece',
    zone: 'red_sea_north',
    zoneName: 'Red Sea',
    lat: 16.8,
    lng: 41.0,
    incidentType: 'Missile Fragmentation Damage',
    severity: 'high',
    source: 'UKMTO',
    description: 'Missile targeting another vessel was intercepted by coalition forces. Fragmentation struck vessel causing topside damage. One crew member with minor lacerations. Vessel diverted to Djibouti.',
  },
  {
    id: 'INC-2604-022',
    date: '2026-04-04',
    vessel: 'BOSPHORUS QUEEN',
    imo: '9812056',
    type: 'Aframax Tanker',
    flag: '🇹🇷 Turkey',
    zone: 'hormuz',
    zoneName: 'Strait of Hormuz',
    lat: 26.3,
    lng: 57.1,
    incidentType: 'IRGC Inspection (Forced)',
    severity: 'high',
    source: 'UKMTO',
    description: 'IRGC boarded vessel under armed escort citing documentation irregularities. Inspection lasted 4.5 hours. Vessel delayed but ultimately released. Turkish MFA lodged formal protest.',
  },
];

// ─── VESSEL HULL VALUE DEFAULTS ───────────────────────────────────────────────

export const VESSEL_TYPES = [
  { id: 'vlcc',     label: 'VLCC Tanker',           hullDefault: 105_000_000 },
  { id: 'suezmax',  label: 'Suezmax Tanker',         hullDefault:  70_000_000 },
  { id: 'aframax',  label: 'Aframax Tanker',         hullDefault:  55_000_000 },
  { id: 'mr',       label: 'MR Tanker',              hullDefault:  42_000_000 },
  { id: 'cape',     label: 'Capesize Bulk Carrier',  hullDefault:  62_000_000 },
  { id: 'panamax',  label: 'Panamax Bulk Carrier',   hullDefault:  37_000_000 },
  { id: 'supra',    label: 'Supramax Bulk Carrier',  hullDefault:  30_000_000 },
  { id: 'handy',    label: 'Handysize Bulk Carrier',  hullDefault:  25_000_000 },
  { id: 'cont8k',   label: 'Container 8,000 TEU',    hullDefault:  90_000_000 },
  { id: 'cont14k',  label: 'Container 14,000 TEU',   hullDefault: 145_000_000 },
  { id: 'lng',      label: 'LNG Carrier',            hullDefault: 225_000_000 },
  { id: 'lpg',      label: 'LPG Carrier',            hullDefault:  85_000_000 },
];

// ─── CAPE REROUTE COMPARISON DATA ─────────────────────────────────────────────

export const CAPE_COMPARISON = {
  hormuz: {
    viaThreatZone: { label: 'Via Hormuz (direct)', extraDays: 0, warRiskApplies: true },
    viaCape:       { label: 'Via Cape of Good Hope', extraDays: 12, bunkerExtra: 280, charterExtra: 120_000, warRiskApplies: false },
  },
  red_sea: {
    viaThreatZone: { label: 'Via Red Sea / Suez', extraDays: 0, warRiskApplies: true },
    viaCape:       { label: 'Via Cape of Good Hope', extraDays: 10, bunkerExtra: 235, charterExtra: 100_000, warRiskApplies: false },
  },
};

// ─── GLOBAL METRICS ───────────────────────────────────────────────────────────

export const computeRiskMetrics = () => {
  const activeZones = THREAT_ZONES.filter(z => ['critical','high'].includes(z.risk)).length;
  const totalZones  = THREAT_ZONES.length;

  const now = new Date();
  const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
  const recent = INCIDENTS.filter(i => new Date(i.date) >= thirtyDaysAgo);

  const prevMonth = INCIDENTS.filter(i => {
    const d = new Date(i.date);
    return d < thirtyDaysAgo && d >= new Date(now - 60 * 24 * 60 * 60 * 1000);
  });

  const hormuzZone     = THREAT_ZONES.find(z => z.id === 'hormuz');
  const lastHormuz     = INCIDENTS.filter(i => i.zone === 'hormuz').sort((a,b)=>new Date(b.date)-new Date(a.date))[0];
  const daysSinceHormuz = lastHormuz
    ? Math.floor((now - new Date(lastHormuz.date)) / (1000 * 60 * 60 * 24))
    : null;

  return {
    activeZones,
    totalZones,
    attacksThisMonth: recent.length,
    attacksLastMonth: prevMonth.length,
    attacksChange:    recent.length - prevMonth.length,
    hormuzPremiumTypical: hormuzZone?.premium.typical,
    daysSinceLastHormuz:  daysSinceHormuz,
    criticalIncidents: recent.filter(i=>i.severity==='critical').length,
  };
};

// ─── MAIN EXPORT ──────────────────────────────────────────────────────────────

export const getMockData = () => ({
  source:      'mock',
  lastUpdated: new Date().toISOString(),
  dataDate:    '2026-04-04',
  zones:       THREAT_ZONES,
  incidents:   INCIDENTS,
  vesselTypes: VESSEL_TYPES,
  metrics:     computeRiskMetrics(),
  disclaimer:  'Demo data — representative April 2026 maritime security situation. Connect Dryad Global or UKMTO feed for live intelligence.',
});
